import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchCouplesCount, fetchPublicStories } from '@/utils/successStories';
import { localizedDigits } from '@/utils/arabicDigits';

/**
 * Real stories from real members, where they agreed to be shown.
 *
 * Renders nothing until there is something true to show: no story is
 * invented here (the examples on /success-stories are labelled as such), and
 * the couples counter is null on the server until it is worth saying.
 */
const SuccessStoriesStrip = ({ onShareStory, className = '' }) => {
  const { t, i18n } = useTranslation();
  const [stories, setStories] = useState([]);
  const [couples, setCouples] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicStories('website').then((rows) => { if (!cancelled) setStories(rows); }).catch(() => {});
    fetchCouplesCount().then((n) => { if (!cancelled) setCouples(n); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const hasContent = stories.length > 0 || couples !== null;
  // With a share button the strip is also the invitation, so it shows even
  // before the first story; without one there is nothing true to show yet.
  if (!hasContent && !onShareStory) return null;

  const stageLabel = {
    married: t('stories.stageMarried'),
    engaged: t('stories.stageEngaged'),
    getting_to_know: t('stories.stageGettingToKnow'),
  };

  return (
    <section className={`space-y-4 ${className}`} data-testid="success-stories-strip">
      {hasContent && (
        <div className="flex flex-wrap items-end justify-between gap-2">
          <h2 className="text-2xl font-bold text-wefaqna-navy">{t('stories.stripTitle')}</h2>
          {couples !== null && (
            <p className="text-sm font-semibold text-wefaqna-forest" data-testid="couples-counter">
              {t('stories.stripCouples', { formatted: localizedDigits(String(couples), i18n.language) })}
            </p>
          )}
        </div>
      )}
      {stories.length > 0 && (
        <div className="flex snap-x gap-4 overflow-x-auto pb-2">
          {stories.map((story) => (
            <article key={story.id} className="w-72 shrink-0 snap-start rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-2" data-testid="public-story">
              <Quote className="h-5 w-5 text-wefaqna-gold" aria-hidden="true" />
              <p className="text-sm text-gray-800 whitespace-pre-line line-clamp-6">{story.story}</p>
              <p className="text-xs text-gray-500">
                {[story.name, story.city, stageLabel[story.stage]].filter(Boolean).join(' · ')}
              </p>
            </article>
          ))}
        </div>
      )}
      {onShareStory && (
        <Button type="button" variant="outline" onClick={onShareStory} className="gap-2" data-testid="strip-share-story">
          <Heart className="h-4 w-4" aria-hidden="true" />
          {t('stories.stripShareCta')}
        </Button>
      )}
    </section>
  );
};

export default SuccessStoriesStrip;
